import React, { useEffect, useState } from 'react';
import {
  Container,
  Heading,
  Box,
  FormLabel,
  Stack,
  Button,
  ButtonGroup,
  IconButton,
  Flex,
  Center,
} from '@chakra-ui/react';
import { debounce } from 'lodash';

import {
  EmailShortNames,
  NotificationsEmail,
  ShortNames,
  TravelFormQuery,
  TravelRequestResult,
} from 'api/types/types';
import {
  DateRangePicker,
  Input,
  Select,
  Table,
} from '@landsvirkjun/react.common';
import { PageHeader } from 'routes/layouts/pageHeader';
import { columns } from './AdminNotificationsOverviewTableModels';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert } from 'components/Alert/Alert';
import moment, { Moment } from 'moment';
import { Spinner } from 'components/Spinner';
import { NoData } from 'components/NoData/NoData';
import { navigateFromRow } from 'routes/travelRequestsOverview/OverviewTable/navigateFromRow';
import { IconArrowLeft, IconArrowRight, IconCalendar } from 'components/Icons';
import '../../../styles/DateRangePicker.scss';
import { useGetAllNotificationEmailQuery } from 'api/services/Notifications';
import { EMAIL_OPTIONS, EMAIL_STATUS_MAPPER } from 'utils/emailUtils';

type DateProps = {
  startDate: Moment | null;
  endDate: Moment | null;
};
type EmailOption = {
  label: string;
  value: EmailShortNames;
};

export const AdminNotificationsOverview = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qSearch = searchParams.get('search');
  const qDateFrom = searchParams.get('dateFrom');
  const qDateTo = searchParams.get('dateTo');

  const [selectedDates, setSelectedDates] = useState<{
    startDate: Moment | null;
    endDate: Moment | null;
  }>({
    startDate: moment().subtract(1, 'month').startOf('month'),
    endDate: moment().endOf('month'),
  });
  // const [searchValue, setSearchValue] = useState<string | undefined>('');

  const [searchText, setSearchText] = useState<string | undefined>(
    qSearch || ''
  );

  const [emailShortName, setEmailShortName] = useState<
    EmailShortNames | undefined
  >('TRAVEL_AUTHORIZE');

  const [emailOption, setEmailOption] = useState<EmailOption | undefined>(
    EMAIL_OPTIONS[0] as EmailOption
  );

  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >(null);
  const [notificationsEmailData, setNotificationEmailData] = useState<
    NotificationsEmail[]
  >([]);

  const {
    data,
    refetch: fetchAllNotifications,
    isError,
    isFetching,
  } = useGetAllNotificationEmailQuery(
    moment(selectedDates.startDate).format('YYYY-MM-DD'),
    selectedDates.endDate
      ? moment(selectedDates.endDate).format('YYYY-MM-DD')
      : moment(selectedDates.startDate).add(1, 'months').format('YYYY-MM-DD'),
    emailShortName,
    searchText
  );

  useEffect(() => {
    if (data) {
      const convert = data.emails as any;
      convert.forEach((item): any => {
        item.dateSent = new Date(item.dateSent);
      });
      setNotificationEmailData(
        convert.sort((a: any, b: any) => a.dateSent - b.dateSent)
      );
    }
  }, [data]);

  useEffect(() => {
    if (qDateFrom || qDateTo) {
      const dates = {
        startDate: qDateFrom ? moment(qDateFrom) : selectedDates.startDate,
        endDate: qDateTo ? moment(qDateTo) : selectedDates.endDate,
      };
      setSelectedDates(dates);
    }
  }, [qDateFrom, qDateTo]);

  useEffect(() => {
    if (qSearch) {
      setSearchText(qSearch);
    } else {
      setSearchText('');
    }
  }, [qSearch]);

  // useEffect(() => {
  //   if (emailOption) {
  //     setEmailShortName(emailOption.value);
  //   }
  // }, [emailOption]);

  const updateParams = (name: string, value: any): void => {
    searchParams.set(name, value);
    setSearchParams(searchParams, { replace: true });
  };

  const handleDateRangeChange = (dates: DateProps): void => {
    updateParams('dateFrom', moment(dates.startDate).format('YYYY-MM-DD'));
    updateParams('dateTo', moment(dates.endDate).format('YYYY-MM-DD'));

    // setSearchParams({
    //   dateFrom: moment(dates.startDate).format('YYYY-MM-DD'),
    //   dateTo: moment(dates.endDate).format('YYYY-MM-DD'),
    // });
    // setSelectedDates(dates);
  };

  const handleCustomFocusedInput = (
    input: 'startDate' | 'endDate' | null
  ): void => {
    setFocusedInput(input);
  };

  const handleRowClick = (row: TravelFormQuery): void => {
    const {
      id,
      status: reqStatus,
      erpId,
    } = row.original as TravelRequestResult;
    const { shortName } = reqStatus;
    navigateFromRow(id, '', shortName, erpId, navigate, true);
  };

  const handleInputChange = debounce((value) => {
    updateParams('search', value);
    // setSearchParams({ search: value });
  }, 500);

  if (isError) {
    return (
      <Box as="div" mt={10}>
        <Alert
          errorText="Ekki tókst að sækja yfirlit yfir ferðabeiðnir"
          status="error"
        />
      </Box>
    );
  }

  const handleStatusChange = (selected: any): void => {
    setEmailOption(EMAIL_STATUS_MAPPER[selected]);
    setEmailShortName(selected);
  };

  const TabTable = (): JSX.Element =>
    isFetching ? (
      <Spinner delay={100} isFixed={false} />
    ) : (
      <Table
        columns={columns}
        data={notificationsEmailData}
        withPagination={notificationsEmailData.length > 30}
        // enableRowClick
        // onRowClick={handleRowClick}
      />
    );

  return (
    <Container maxW="container.xl" marginStart="ltr" pl={0}>
      <PageHeader
        left={
          <Heading as="h2" size="lg">
            Yfirlit tilkynninga
          </Heading>
        }
      />
      <Container maxW="none" mt={12} marginStart="ltr" p={0}>
        <Flex
          gap={10}
          mb={2}
          marginBottom={'50px'}
          minWidth="max-content"
          justifyContent="space-between"
          alignItems={'end'}
        >
          <Center alignItems={'end'} gap={10}>
            <Box>
              <FormLabel>Leita eftir netfangi</FormLabel>
              <Input
                placeholder="Byrjaðu að slá inn netfang til að hefja leit"
                onChange={(event): void => {
                  const { value } = event.target;
                  handleInputChange(value);
                }}
              />
            </Box>
            <Box width={250}>
              <FormLabel>Tilkynningar</FormLabel>
              <Select
                // @ts-ignore
                value={emailOption}
                onChange={handleStatusChange}
                placeholder="Tegund tilkynningar"
                options={EMAIL_OPTIONS}
                // defaultValue={emailOption ? emailOption : null}
              />
            </Box>
          </Center>
          <Center>
            <Box
              display={'flex'}
              justifyContent="flex-end"
              flexDirection={'column'}
            >
              <Stack direction="row">
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(): void =>
                      handleDateRangeChange({
                        startDate: moment(selectedDates.startDate)
                          .subtract(1, 'month')
                          .endOf('month'),
                        endDate: moment(selectedDates.endDate),
                      })
                    }
                  >
                    <IconArrowLeft />
                  </Button>
                  {Array(3)
                    .fill('')
                    .map((_, i) => (
                      <Button
                        key={i}
                        variant="secondary"
                        size="sm"
                        onClick={(): void =>
                          handleDateRangeChange({
                            startDate: moment(new Date())
                              .subtract(i, 'month')
                              .startOf('month'),
                            endDate: moment(new Date())
                              .subtract(i, 'month')
                              .endOf('month'),
                          })
                        }
                      >
                        {moment(new Date()).subtract(i, 'month').format('MMM')}
                      </Button>
                    ))
                    .reverse()}
                  {/* <Button
                    variant="secondary"
                    size="sm"
                    onClick={(): void =>
                      setSelectedDates({
                        startDate: moment(selectedDates.startDate),
                        endDate: moment(selectedDates.endDate)
                          .add(1, 'month')
                          .endOf('month'),
                      })
                    }
                  >
                    <IconArrowRight />
                  </Button> */}
                </ButtonGroup>
                <Box>
                  <IconButton
                    variant="secondary"
                    aria-label="Veldu dagsetningu"
                    size="sm"
                    icon={<IconCalendar />}
                    onClick={(): void => handleCustomFocusedInput('startDate')}
                  ></IconButton>
                  <Box as="div" className="hiddenPicker">
                    <DateRangePicker
                      startDate={selectedDates.startDate}
                      endDate={selectedDates.endDate}
                      onChange={handleDateRangeChange}
                      inputFocused={focusedInput}
                      handleCustomFocusedInput={handleCustomFocusedInput}
                    />
                  </Box>
                </Box>
                {/* <IconButton
                  variant="secondary"
                  aria-label="Hreinsa síu"
                  title="Hreinsa síu"
                  size="sm"
                  icon={<IconRefresh />}
                  onClick={(): void => clearFilter()}
                /> */}
              </Stack>
              <Box mt={3} display="flex" justifyContent={'flex-end'}>
                <div>
                  {' '}
                  {selectedDates?.startDate
                    ? moment(selectedDates.startDate).format('Do MMM')
                    : ''}{' '}
                  <small style={{ color: '#939598' }}> - </small>{' '}
                  {selectedDates?.endDate
                    ? moment(selectedDates.endDate).format('Do MMM YYYY')
                    : ''}
                </div>
              </Box>
            </Box>
          </Center>
        </Flex>
        {data && (
          <Box
            mt={3}
            display="flex"
            justifyContent={'flex-end'}
            style={{ color: '#939598' }}
          >
            Fjöldi sendinga: {!data?.emails && ' +'}
          </Box>
        )}

        {isFetching ? (
          <Spinner delay={100} isFixed={false} />
        ) : notificationsEmailData.length > 0 ? (
          <TabTable />
        ) : (
          <NoData text="Engar ferðabeiðnir fundust með tilgreindum leitarskilyrðum" />
        )}
      </Container>
    </Container>
  );
};
